<template>
  <!--
    Membership / Wallet column: the membership badge (VIP / Superfan / Free) +
    the wallet token balance, then the single channel-wide Upgrade / Top up
    action.
  -->
  <div class="flex min-w-0 flex-1 flex-col gap-3">
    <span class="text-[15px] font-semibold uppercase leading-[22px] text-text">
      {{ t("channel.profileCard.membershipWallet") }}
    </span>

    <div class="flex flex-row flex-wrap items-center gap-3">
      <span
        class="inline-flex h-8 items-center gap-1 rounded-full bg-surface px-2 py-1 text-[13px] font-semibold leading-[20px] text-text"
        data-testid="channel-profile-card-membership"
      >
        <!--
          Premium-tier badge: the Figma MEMBERSHIP / WALLET "VIP" pill shows a
          small red premium mark immediately left of the label. Render it ONLY
          for a PAID tier (not Free, not superfan-only) so the free pill stays
          text-only. `text-icon-brand` keeps it theme-adaptive (brand red);
          `size-4` matches the pill's text height without affecting alignment.
        -->
        <Icon
          v-if="hasPaidTier"
          icon="mdi:star-box"
          class="size-4 shrink-0 text-icon-brand"
          aria-hidden="true"
          data-testid="channel-profile-card-membership-badge"
        />
        {{ membershipLabel }}
      </span>

      <span
        v-if="walletTokenLabel"
        class="inline-flex h-8 items-center gap-1 rounded-full bg-surface px-2 py-1 text-[13px] font-semibold leading-[20px] text-text drop-shadow-[1px_4px_10.5px_rgba(0,0,0,0.13)]"
        data-testid="channel-profile-card-wallet-line"
      >
        <Icon
          icon="mdi:wallet-outline"
          class="size-4 text-icon-secondary"
          aria-hidden="true"
        />
        {{
          t("channel.profileCard.tokenBalance", {
            amount: formattedTokenBalance,
            tokenLabel: walletTokenLabel,
          })
        }}
      </span>
    </div>

    <!--
      Single "Upgrade / Top up" button → ONE channel-wide upgrade intent. The
      card does NOT decide membership-vs-token here (that depends on channel
      CAPABILITIES, which the auth orchestrator owns): it emits `upgrade` and the
      orchestrator resolves the available paths (0 → no-op, single → direct,
      multiple → chooser). The button only renders when the channel actually
      offers at least one upgrade path.

      Styling matches the Figma: a WHITE pill button (`bg-surface` +
      `border-default`), NOT brand-red. mt-auto pins it to the column bottom so
      the three action buttons baseline-align across columns.
    -->
    <button
      v-if="hasAnyUpgradePath"
      type="button"
      class="mt-auto flex h-[50px] w-full items-center justify-center rounded-full border border-border bg-surface px-4 text-[15px] font-medium leading-[20px] text-text transition-colors hover:bg-black/5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-border-brand"
      data-testid="channel-profile-card-upgrade"
      @click="emit('upgrade')"
    >
      {{ t("channel.profileCard.upgradeOrTopUp") }}
    </button>
  </div>
</template>

<script setup lang="ts">
import { useI18n } from "vue-i18n";
import { Icon } from "@iconify/vue";

defineProps<{
  membershipLabel: string;
  hasPaidTier: boolean;
  walletTokenLabel: string | null;
  formattedTokenBalance: string;
  hasAnyUpgradePath: boolean;
}>();

const emit = defineEmits<{
  upgrade: [];
}>();

const { t } = useI18n();
</script>
